import React, { useMemo, useState } from 'react'
import { Eye, EyeOff, Plus, FlaskConical, Box, Shapes, ExternalLink } from 'lucide-react'
import { useLessonBuilder } from '../store/useLessonBuilder'
import { genId } from '../lib/id'
import { SimulationLibrary, type SimEntry } from '../../../services/simulation/SimulationLibrary'
import InteractiveContentRenderer from '../../../components/interactive/InteractiveContentRenderer'
import GeoGebraSimulation from '../../../components/interactive/shared/simulations/GeoGebraSimulation'

const KIND_META: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
  canvas:   { label: 'Mô phỏng', cls: 'bg-blue-100 text-blue-700',     icon: <Box className="w-3.5 h-3.5" /> },
  geogebra: { label: 'GeoGebra', cls: 'bg-violet-100 text-violet-700', icon: <Shapes className="w-3.5 h-3.5" /> },
  video:    { label: 'Video',    cls: 'bg-amber-100 text-amber-700',   icon: <Box className="w-3.5 h-3.5" /> },
  lab:      { label: 'Thí nghiệm IoT', cls: 'bg-emerald-100 text-emerald-700', icon: <FlaskConical className="w-3.5 h-3.5" /> },
}

const ResourcePanel: React.FC = () => {
  const { lesson, addPreparedCard } = useLessonBuilder()
  const lop = Number(lesson.meta.lop), chuong = Number(lesson.meta.chuong), bai = Number(lesson.meta.bai)
  const ready = !!(lop && chuong && bai)
  const entries = useMemo<SimEntry[]>(
    () => (ready ? SimulationLibrary.getSimsByLesson(lop, chuong, bai) : []),
    [ready, lop, chuong, bai]
  )
  const [previewId, setPreviewId] = useState<string | null>(null)
  const [insertedIds, setInsertedIds] = useState<Set<string>>(new Set())

  const insert = (e: SimEntry) => {
    const card = { id: genId(), ...SimulationLibrary.toLessonCard(e) } as any
    addPreparedCard(card, /chính/i)
    setInsertedIds((s) => new Set(s).add(e.id))
  }

  const renderPreview = (e: SimEntry) => {
    if (e.kind === 'geogebra' && e.ggbFile) {
      return <GeoGebraSimulation filename={e.ggbFile} height={360} />
    }
    if (e.kind === 'lab') {
      return (
        <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">
          <p className="mb-3">{e.description}</p>
          <a href={e.url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white font-bold hover:bg-emerald-700">
            <ExternalLink className="w-4 h-4" /> Mở thí nghiệm tại Lab
          </a>
        </div>
      )
    }
    // canvas (interactive_oscillation / interactive_vt_graph / ...)
    return <InteractiveContentRenderer content={[SimulationLibrary.toContentBlock(e)]} />
  }

  return (
    <div>
      <h3 className="flex items-center gap-1.5 text-xs font-black text-emerald-700 uppercase tracking-wider mb-2">
        <Shapes size={14} /> Tài nguyên tương tác theo bài
      </h3>
      {!ready ? (
        <p className="text-xs text-gray-400">Hãy nhập <b>Lớp / Chương / Bài</b> ở phần "Thông tin bài" để xem mô phỏng, GeoGebra và thí nghiệm Lab phù hợp.</p>
      ) : entries.length === 0 ? (
        <p className="text-xs text-gray-400">Chưa có tài nguyên cho bài này (Lớp {lop} · Chương {chuong} · Bài {bai}).</p>
      ) : (
        <div className="space-y-2">
          {entries.map((e) => {
            const meta = KIND_META[e.kind] || KIND_META.canvas
            const open = previewId === e.id
            const inserted = insertedIds.has(e.id)
            return (
              <div key={e.id} className="rounded-xl border border-gray-200 bg-white">
                <div className="flex items-center gap-2 p-2.5">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${meta.cls}`}>{meta.icon}{meta.label}</span>
                  <span className="flex-1 text-sm text-gray-800 truncate" title={e.title}>{e.title}</span>
                  <button onClick={() => setPreviewId(open ? null : e.id)}
                    className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded" title="Xem trước">
                    {open ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button onClick={() => insert(e)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold ${inserted ? 'bg-green-100 text-green-700' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
                    title="Chèn vào Hoạt động chính">
                    <Plus className="w-3.5 h-3.5" /> {inserted ? 'Đã chèn' : 'Chèn'}
                  </button>
                </div>
                {open && <div className="border-t border-gray-100 p-3 bg-gray-50/40">{renderPreview(e)}</div>}
              </div>
            )
          })}
          <p className="text-[11px] text-gray-400 mt-1">💡 Xem trước để chọn tài nguyên phù hợp, rồi <b>Chèn</b> vào bài; sau đó tinh chỉnh trong canvas như thường.</p>
        </div>
      )}
    </div>
  )
}

export default ResourcePanel
